import { serverPostDeriveFeedbackStyle } from "api/api-server-ai";
import { DEFAULT_MODEL_FOR_ASSESSMENT_QUERY } from "components/ai/components/AISettings";
import { t } from "i18n/i18n";
import { type AppDispatch, type RootState, store } from "store/index";
import { selectIsRecordingFeedbackStyle, selectUserSelectedAIModel } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { toastErrorMessage, toastSuccessMessage } from "utils/alerts";
import { compactMap } from "utils/collections";
import { rollbarAndLogError } from "utils/logger";
import { isAssessmentAIFeedbackStyleEnabled } from "utils/waffle-flags";

export function getNonEmptyFeedbackSamplesToDerive(state: RootState): string[] {
  return compactMap(state.ai.assessmentFeedbackStyleSamples, (row) => {
    const text = row.feedback.trim();
    return text.length > 0 ? text : null;
  });
}

/** After a successful manual assessment save, store comment text for style inference when recording is on. */
export function maybeRecordFeedbackStyleSample(submissionId: string, comment: string): void {
  const state = store.getState();
  if (!isAssessmentAIFeedbackStyleEnabled()) {
    return;
  }
  const feedbackText = comment.trim();
  if (!feedbackText || !selectIsRecordingFeedbackStyle(state)) {
    return;
  }
  store.dispatch(
    aiStateActions.addFeedbackStyleSample({
      submissionId,
      feedback: feedbackText,
    }),
  );
}

/**
 * Turns off "record feedback for style" — if there are samples, infers style via API first (same as settings toggle off).
 */
export async function stopRecordingFeedbackStyle(dispatch: AppDispatch): Promise<void> {
  const state = store.getState();
  const toSend = getNonEmptyFeedbackSamplesToDerive(state);
  if (toSend.length === 0) {
    dispatch(aiStateActions.setIsRecordingFeedbackStyle(false));
    return;
  }
  const model = selectUserSelectedAIModel(state) ?? DEFAULT_MODEL_FOR_ASSESSMENT_QUERY;
  dispatch(aiStateActions.setIsDerivingFeedbackStyle(true));
  try {
    await serverPostDeriveFeedbackStyle(toSend, model);
    dispatch(aiStateActions.clearRecordedFeedbackStyle());
    dispatch(aiStateActions.setIsRecordingFeedbackStyle(false));
    toastSuccessMessage(t("home.settings.assessment_ai_feedback_style.derive_success"));
  } catch (error) {
    rollbarAndLogError("stopRecordingFeedbackStyle", error);
    toastErrorMessage(t("home.settings.assessment_ai_feedback_style.derive_error"));
    throw error;
  } finally {
    dispatch(aiStateActions.setIsDerivingFeedbackStyle(false));
  }
}
